/**
 * Helpers for turning a bookie key into a link that opens the book's native
 * app on mobile (when installed) and a new tab on desktop.
 *
 * IMPORTANT – Apple's "click-gate"
 * --------------------------------
 * iOS Universal Links ONLY fire on a real user tap of an `<a href>` element.
 * They do not fire when you set `window.location.href` from JavaScript, even
 * from inside a click handler.  See:
 *   https://developer.apple.com/documentation/technotes/tn3155-debugging-universal-links
 *   https://help.branch.io/developer-hub/docs/ios-universal-links  ("click-gate")
 *
 * Therefore we never navigate programmatically on iOS.  Instead we render a
 * real `<a>` with the https Universal-Link URL, `target="_self"`, and let
 * the browser's native click handling trigger the system link resolver.
 *
 * Android uses `intent://…#Intent;package=…;S.browser_fallback_url=…;end`
 * which opens the app if installed or silently falls back to the web URL.
 * Chrome is happy to be `window.location.href`-navigated to an intent URL,
 * so we override the click on Android only.
 *
 * Desktop behaves the same as before – new tab with the web URL.
 */

import React from "react";
import { bookieSignUpLinks } from "../components/products/common/bookie-image";
import { isAndroid, isMobileDevice } from "./device";

interface BookieAppLink {
  /** Android package identifier (most common US variant). */
  androidPackage?: string;
}

/**
 * Android package names for the most common US sportsbook / DFS apps.
 * iOS is handled exclusively via Universal Links on the https URL –
 * adding a custom scheme (e.g. `dkng://`) would trigger Safari's
 * "address is invalid" dialog when the app isn't installed.
 */
const BOOKIE_APP_LINKS: Record<string, BookieAppLink> = {
  draftkings: { androidPackage: "com.draftkings.sportsbook" },
  fanduel: { androidPackage: "com.fanduel.android.sportsbook" },
  betmgm: { androidPackage: "com.playmgm.nj.sports" },
  williamhill_us: { androidPackage: "com.williamhill.US" },
  betrivers: { androidPackage: "com.rushstreet.betrivers" },
  pointsbetus: { androidPackage: "com.pointsbet" },
  espnbet: { androidPackage: "com.pngaming.espn.portnj" },
  hardrockbet: { androidPackage: "com.hardrockdigital.sportsbook" },
  fanatics: { androidPackage: "com.fanatics.sportsbook" },
  prizepicks: { androidPackage: "com.prizepicks.app" },
  underdog: { androidPackage: "com.underdogsports.fantasy" },
  pick6: { androidPackage: "com.draftkings.dkpick6mobile" },
  fliff: { androidPackage: "com.getfliff.fliff" },
};

function resolveDeepKey(bookieKey: string): string {
  const lower = bookieKey.toLowerCase();
  if (lower.includes("hardrock") || lower.includes("hard_rock")) return "hardrockbet";
  return lower;
}

function buildAndroidIntentUrl(androidPackage: string, webUrl: string): string {
  return (
    `intent://#Intent;` +
    `package=${androidPackage};` +
    `scheme=https;` +
    `S.browser_fallback_url=${encodeURIComponent(webUrl)};` +
    `end`
  );
}

export interface BookieLinkProps {
  href: string;
  target: "_self" | "_blank";
  rel: string;
  onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * Returns props to spread onto an `<a>` element so that clicking it opens
 * the bookie in the native app when possible, or the website otherwise.
 *
 * Usage:
 * ```tsx
 * const props = getBookieLinkProps("draftkings");
 * return <a {...props}>Open DraftKings</a>;
 * ```
 *
 * This MUST be rendered on an actual `<a>` element for iOS Universal Links
 * to work – rendering a `<button>` / `<div>` and calling `window.location`
 * programmatically won't trigger the app on iOS, even if installed.
 */
export function getBookieLinkProps(bookieKey: string): BookieLinkProps {
  const key = resolveDeepKey(bookieKey);
  const webUrl = bookieSignUpLinks[key] || bookieSignUpLinks[bookieKey] || "#";
  const mobile = isMobileDevice();
  const android = isAndroid();

  const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Preserve modifier-click defaults (cmd/ctrl/middle-click → new tab).
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
    if (!webUrl || webUrl === "#") {
      e.preventDefault();
      return;
    }

    if (android) {
      const link = BOOKIE_APP_LINKS[key];
      if (link?.androidPackage) {
        // Use an intent URL so Android opens the app if installed, otherwise
        // falls back silently to the https URL.
        e.preventDefault();
        window.location.href = buildAndroidIntentUrl(link.androidPackage, webUrl);
        return;
      }
    }

    // iOS and every other case – do NOT preventDefault.  Let the browser
    // handle the native `<a>` tap so Universal Links can intercept.
  };

  return {
    href: webUrl,
    // Mobile: same tab so iOS Universal Links / Android App Links fire.
    // Desktop: keep the new-tab behaviour.
    target: mobile ? "_self" : "_blank",
    rel: "noopener noreferrer",
    onClick,
  };
}

/**
 * Imperative helper for programmatic navigation (e.g. from a keyboard
 * shortcut).  Prefer `getBookieLinkProps` with an `<a>` element whenever
 * possible, because this path cannot trigger iOS Universal Links on its
 * own – on iOS it always loads the website in Safari.
 */
export function openBookie(bookieKey: string): void {
  if (!bookieKey) return;
  const key = resolveDeepKey(bookieKey);
  const webUrl = bookieSignUpLinks[key] || bookieSignUpLinks[bookieKey] || "";
  if (!webUrl) return;

  const mobile = isMobileDevice();
  const android = isAndroid();

  if (!mobile) {
    window.open(webUrl, "_blank", "noopener,noreferrer");
    return;
  }

  if (android) {
    const link = BOOKIE_APP_LINKS[key];
    if (link?.androidPackage) {
      window.location.href = buildAndroidIntentUrl(link.androidPackage, webUrl);
      return;
    }
  }

  window.location.href = webUrl;
}
